import React, { useEffect, useRef, useState } from "react";

// Hooks
import useResizeObserver from "use-resize-observer";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "app/hooks";

// Utils
import { debounce, differenceBy, intersectionBy, isEmpty, isEqual, orderBy } from "lodash";

// Components
import { OrderTableBy, OrderTable, Order } from "components/SortMenu/SortMenu";
import {
    addUserToGroup,
    fetchClientHubByOrgId,
    getSelectableUsersForClientHub,
    usersForClientHubFilters,
    getPendingInvitations,
    ClientHubByIdResponse,
} from "services";
import { getLoaderState, toggleLoader } from "store/slice/UI";
import { ConvertedUsersList, ConvertedUsersListData, convertUsersList } from "./adapters/converters";
import { initialPagination } from "constants/constants";
import { urlPaths } from "enums/urlPaths";

// types
import { TPendingInvitation } from "pages/HubManagement/components/InviteUsers/InviteUsersModal.types";
import { TPersonInfo } from "components/Team/Team.types";

const DEFAULT_PAGE_SIZE = 24;
const FILTERS_INITIAL_VALUE = { filter_user_name: "", filter_company_name: "" };
const DEFAULT_SORT = { columnId: "name", direction: "asc" as const };

const initialPersonInfo = {
    role: "",
    id: -1,
    info: undefined,
};

const useHubAddUsers = (): {
    users: ConvertedUsersList;
    selectedUsers: ConvertedUsersListData[];
    filters: { filter_user_name: string; filter_company_name: string };
    usersSort: OrderTableBy;
    selectedUsersSort: OrderTableBy;
    paginationTextInputRef: React.RefObject<HTMLInputElement>;
    activeTab: number;
    inviteModalOpen: boolean;
    inputPage: number | string;
    selectAllCheckboxes: Record<string, boolean>;
    pendingInvitations: TPendingInvitation[];
    org_id: string | undefined;
    isFetching: boolean;
    toggleShowWorkHistory: boolean;
    WHAnchorEl: HTMLElement | null;
    personInfo: TPersonInfo;
    filtersInitialValue: typeof FILTERS_INITIAL_VALUE;
    hub: ClientHubByIdResponse;
    usersWidthPercentage: number;
    selectedUsersWidthPercentage: number;
    selectedUsersMaxWidth: number;
    tablesWrapperRef: React.RefCallback<HTMLElement>;
    handleFetchData: (params?: Partial<typeof usersForClientHubFilters>) => void;
    handleUsersSort: (direction: OrderTable, columnId: string | number) => void;
    handleSelectUsersSort: (direction: Order, columnId: string | number) => void;
    handleFiltersChange: (event: any) => void;
    handleAddUser: (
        event: React.MouseEvent<HTMLElement, MouseEvent>,
        userData: ConvertedUsersListData,
        isUserSelected: boolean
    ) => void;
    handleRemoveUser: (event: React.MouseEvent<HTMLElement, MouseEvent>, userData: ConvertedUsersListData) => void;
    handleAddMultipleUsers: () => void;
    handleRemoveAllUsers: () => void;
    handlePageChange: (page: number) => Promise<void>;
    handleInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleOnBlur: () => void;
    handleOnKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    toggleCheckUser: (event: React.ChangeEvent<HTMLInputElement>, userId: number) => void;
    toggleCheckAllUsers: (event: React.ChangeEvent<HTMLInputElement>) => void;
    toggleCheckSelectedUser: (event: React.ChangeEvent<HTMLInputElement>, userId: number) => void;
    toggleCheckAllSelectedUsers: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleOpenInvitationModal: () => void;
    handleInviteSelectedUsers: () => void;
    closeInviteModal: () => void;
    handleExitPage: () => void;
    handleSaveUsers: () => void;
    handleClearFilters: () => void;
    onCloseWH: () => void;
    onClickRestoreIcon: (event: React.MouseEvent<HTMLElement>, userId?: number) => void;
} => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { org_id } = useParams();

    const isFetching = useAppSelector(getLoaderState);

    const { ref: tablesWrapperRef, width: tableWrapperWidth } = useResizeObserver();
    const usersWidthPercentage = 72;
    const selectedUsersWidthPercentage = 28;
    const selectedUsersMaxWidth = (tableWrapperWidth || 1) * (selectedUsersWidthPercentage / 100) - 100; // 100 = checkbox, icon, remove icon widths

    const [users, setUsers] = useState<ConvertedUsersList>({
        data: [],
        meta: initialPagination,
        status: "pristine",
    });
    const [hub, setHub] = useState<ClientHubByIdResponse>({} as ClientHubByIdResponse);
    const [selectedUsers, setSelectedUsers] = useState<ConvertedUsersListData[]>([]);

    const [filters, setFilters] = useState(FILTERS_INITIAL_VALUE);
    const previousFilters = useRef(FILTERS_INITIAL_VALUE);

    const [usersSort, setUsersSort] = useState<OrderTableBy>(DEFAULT_SORT);
    const [selectedUsersSort, setSelectedUsersSort] = useState<OrderTableBy<Order>>({} as typeof DEFAULT_SORT);

    // WorkHistory
    const [toggleShowWorkHistory, setToggleShowWorkHistory] = useState(false);
    const [WHAnchorEl, setWHAnchorEl] = useState<HTMLElement | null>(null);
    const [personInfo, setPersonInfo] = useState<TPersonInfo>(initialPersonInfo);

    const currentSortValue = { order_by: `${usersSort.columnId},${usersSort.direction}` };

    const [selectAllCheckboxes, setSelectAllCheckboxes] = useState<Record<string, boolean>>({
        users: false,
        selectedUsers: false,
    });

    const [inputPage, setInputPage] = useState<number | string>(1);
    const previousPageValue = useRef<number | string>(1);
    const paginationTextInputRef = useRef<any>(null);

    const handleFetchData = async (params?: Partial<typeof usersForClientHubFilters>) => {
        if (isEqual(filters, previousFilters.current) && isEmpty(params)) return;

        const requestPayload = {
            ...(params?.filter_user_name ? { filter_user_name: params.filter_user_name } : {}),
            ...(params?.filter_company_name ? { filter_company_name: params.filter_company_name } : {}),
            ...(params?.order_by ? { order_by: params.order_by } : {}),
            page_no: params?.page_no || 1,
            page_size: DEFAULT_PAGE_SIZE,
        };

        try {
            setUsers((data) => ({ ...data, status: "fetching" }));
            const { data } = await getSelectableUsersForClientHub(Number(org_id), requestPayload);
            const convertedData = convertUsersList(data.results);
            setUsers({ data: convertedData, status: "done", meta: data.meta });
        } catch (error) {
            console.error(error);
            setUsers((data) => ({ ...data, status: "error" }));
        }
    };

    // Invitations
    const [inviteModalOpen, setInviteModalOpen] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<number>(0);
    const [pendingInvitations, setPendingInvitations] = useState<TPendingInvitation[]>([]);

    const fetchPendinginvitations = async () => {
        try {
            const { data } = await getPendingInvitations(Number(org_id));

            const pendingWithoutInvite: TPendingInvitation[] = data.pending_users_without_invite.map((user) => ({
                id: user.id,
                recipient_name: user.name,
                recipient_email: user.email,
            }));

            setPendingInvitations([...data.org_invites_sent, ...pendingWithoutInvite]);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        if (inviteModalOpen) {
            fetchPendinginvitations();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inviteModalOpen]);

    // --------------------------------------------------------------------------------------------------------------
    // Filters and Sorting Functions
    // --------------------------------------------------------------------------------------------------------------
    const delayedFilterName = useRef(
        debounce((value: string, filters: typeof FILTERS_INITIAL_VALUE, type: "user_name" | "company_name") => {
            handleFetchData({
                ...filters,
                ...currentSortValue,
                [`filter_${type}`]: value,
                page_size: DEFAULT_PAGE_SIZE,
                page_no: 1,
            });
        }, 500)
    ).current;
    useEffect(() => () => delayedFilterName.cancel(), [delayedFilterName]);

    const handleFiltersChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setInputPage(1);

        if (event.target.name === "user-filter") {
            const newValue = (event as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>).target.value;
            setFilters((currentVal) => ({ ...currentVal, filter_user_name: newValue }));
            delayedFilterName(newValue, filters, "user_name");
        }

        if (event.target.name === "company-filter") {
            const newValue = (event as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>).target.value;
            setFilters((currentVal) => ({ ...currentVal, filter_company_name: newValue }));
            delayedFilterName(newValue, filters, "company_name");
        }
    };

    const handleClearFilters = () => {
        setFilters(FILTERS_INITIAL_VALUE);
        setUsersSort(DEFAULT_SORT);
        handleFetchData({ ...FILTERS_INITIAL_VALUE, order_by: `${DEFAULT_SORT.columnId},${DEFAULT_SORT.direction}` });
    };

    const handleUsersSort = (direction: OrderTable, columnId: string | number) => {
        setUsersSort({ columnId, direction });
        handleFetchData({ ...filters, order_by: `${columnId},${direction}` });
    };

    const handleSelectUsersSort = (direction: Order, columnId: string | number) => {
        setSelectedUsersSort({ columnId, direction });
        setSelectedUsers((users) => orderBy(users, ["name"], [direction]));
    };

    // --------------------------------------------------------------------------------------------------------------
    // Add/Remove Users functions
    // --------------------------------------------------------------------------------------------------------------
    const resetUserCheckbox = (userData?: ConvertedUsersListData) => {
        setUsers((users) => ({
            ...users,
            data: users.data.map((member) => {
                const isCurrentUser = userData?.id === member.id ? false : member.checked;
                return {
                    ...member,
                    checked: userData ? isCurrentUser : false,
                };
            }),
        }));
    };

    const handleAddUser = (
        event: React.MouseEvent<HTMLElement, MouseEvent>,
        userData: ConvertedUsersListData,
        isUserSelected: boolean
    ) => {
        event.stopPropagation();
        if (isUserSelected) {
            setSelectedUsers((members) => members.filter((member) => member.id !== userData.id));
        } else {
            setSelectedUsers((members) => [...members, { ...userData, checked: false }]);
        }
        resetUserCheckbox(userData);
    };

    const handleRemoveUser = (event: React.MouseEvent<HTMLElement, MouseEvent>, userData: ConvertedUsersListData) => {
        event.stopPropagation();
        setSelectedUsers((members) => members.filter((member) => member.id !== userData.id));
    };

    const handleAddMultipleUsers = () => {
        const chekedUsers = users.data.filter((user) => user.checked);
        const newUsers = differenceBy(chekedUsers, selectedUsers, "id").map((user) => ({ ...user, checked: false }));

        if (newUsers) {
            setSelectedUsers((currentUsers) => [...currentUsers, ...newUsers]);
        }

        setSelectAllCheckboxes((prevData) => ({ ...prevData, users: false }));
        resetUserCheckbox();
    };

    const handleRemoveAllUsers = () => {
        setSelectAllCheckboxes((prevData) => ({ ...prevData, selectedUsers: false }));
        setSelectedUsers([]);
    };

    const toggleCheckUser = (event: React.ChangeEvent<HTMLInputElement>, userId: number) => {
        setUsers((users) => ({
            ...users,
            data: users.data.map((member) => ({
                ...member,
                checked: member.id === userId ? event.target.checked : member.checked,
            })),
        }));
    };

    const toggleCheckAllUsers = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedIds = intersectionBy(users.data, selectedUsers, "id").map((user) => user.id);

        setSelectAllCheckboxes((prevData) => ({ ...prevData, users: event.target.checked }));
        setUsers((users) => ({
            ...users,
            data: users.data.map((member) => ({
                ...member,
                checked: event.target.checked && !selectedIds.includes(member.id),
            })),
        }));
    };

    const toggleCheckSelectedUser = (event: React.ChangeEvent<HTMLInputElement>, userId: number) => {
        setSelectedUsers((members) =>
            members.map((member) => ({
                ...member,
                checked: member.id === userId ? event.target.checked : member.checked,
            }))
        );
    };

    const toggleCheckAllSelectedUsers = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectAllCheckboxes((prevData) => ({ ...prevData, selectedUsers: event.target.checked }));

        setSelectedUsers((members) =>
            members.map((member) => ({
                ...member,
                checked: event.target.checked,
            }))
        );
    };

    // --------------------------------------------------------------------------------------------------------------
    // Invitation modal functions
    // --------------------------------------------------------------------------------------------------------------

    const handleOpenInvitationModal = () => {
        setActiveTab(1);
        setInviteModalOpen(true);
    };

    const handleInviteSelectedUsers = () => {
        setActiveTab(0);
        setInviteModalOpen(true);
    };

    const closeInviteModal = () => {
        setInviteModalOpen(false);
    };

    const handleExitPage = () => {
        navigate(`${urlPaths.HubManagementEditClient}/${org_id}`);
    };

    const handleSaveUsers = async () => {
        if (!hub.group.id || !selectedUsers.length) return;

        try {
            dispatch(toggleLoader(true));
            await Promise.all(
                selectedUsers.map(async (user) => {
                    return await addUserToGroup(user.id, { groups: [hub.group.id] });
                })
            );
            dispatch(toggleLoader(false));
            navigate(`${urlPaths.HubManagementEditClient}/${org_id}`);
        } catch (error) {
            console.error(error);
            dispatch(toggleLoader(false));
        }
    };

    // --------------------------------------------------------------------------------------------------------------
    // Pagination functions
    // --------------------------------------------------------------------------------------------------------------
    const handlePageChange = async (page: number) => {
        if (page < 1 || page > users.meta.pages) {
            return;
        }
        setInputPage(page);
        handleFetchData({ ...filters, ...currentSortValue, page_size: users.meta.page_size, page_no: page });
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.value === "") {
            setInputPage("");
            return;
        }
        const inputValue = Number(event.target.value);
        if (inputValue > 0 && inputValue <= users.meta.pages) {
            setInputPage(inputValue);
        }
    };

    const handleOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            if (inputPage === users.meta.page_no) {
                return;
            }

            paginationTextInputRef.current.blur();
        }

        if (event.key === "Escape") {
            setInputPage(previousPageValue.current || 1);
            setTimeout(() => {
                paginationTextInputRef.current.blur();
            }, 1);
            return;
        }
    };

    const handleOnBlur = () => {
        if (inputPage === "" || inputPage === users.meta.page_no) {
            setInputPage(users.meta.page_no);
            return;
        }

        if (Boolean(inputPage) && Number(inputPage) > 0 && Number(inputPage) <= users.meta.pages) {
            previousPageValue.current = inputPage;
            handlePageChange(Number(inputPage));
        }
    };

    // Worh History Button Functions
    const onClickRestoreIcon = (event: React.MouseEvent<HTMLElement>, userId?: number) => {
        let selectedUser = users.data.find((user) => user.id === userId);

        if (!selectedUser) {
            selectedUser = selectedUsers.find((user) => user.id === userId);
        }

        if (!selectedUser || !selectedUser?.person_id) return;

        setToggleShowWorkHistory(true);
        setPersonInfo({
            role: selectedUser?.position || "",
            id: selectedUser.person_id,
        });
        setWHAnchorEl(event.currentTarget);
    };

    const onCloseWH = () => {
        setToggleShowWorkHistory(false);
        setWHAnchorEl(null);
    };

    // --------------------------------------------------------------------------------------------------------------
    // Initial Fetch
    // --------------------------------------------------------------------------------------------------------------
    useEffect(() => {
        const initialFetch = async () => {
            const promises = [];
            dispatch(toggleLoader(true));

            promises.push(fetchPendinginvitations());

            try {
                const { data } = await fetchClientHubByOrgId(Number(org_id));
                setHub(data);
            } catch (error) {
                console.error(error);
            }

            try {
                const { data } = await getSelectableUsersForClientHub(Number(org_id), {
                    page_size: DEFAULT_PAGE_SIZE,
                    order_by: "name,asc",
                });
                const convertedData = convertUsersList(data.results);
                setUsers({ data: convertedData, status: "done", meta: data.meta });
            } catch (error) {
                console.error(error);
            } finally {
                dispatch(toggleLoader(false));
            }
        };

        if (org_id) {
            initialFetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        users,
        selectedUsers,
        filters,
        usersSort,
        selectedUsersSort,
        inputPage,
        paginationTextInputRef,
        activeTab,
        inviteModalOpen,
        selectAllCheckboxes,
        pendingInvitations,
        org_id,
        isFetching,
        toggleShowWorkHistory,
        WHAnchorEl,
        personInfo,
        filtersInitialValue: FILTERS_INITIAL_VALUE,
        hub,
        usersWidthPercentage,
        selectedUsersWidthPercentage,
        selectedUsersMaxWidth,
        tablesWrapperRef,
        handleFetchData,
        handleUsersSort,
        handleSelectUsersSort,
        handleFiltersChange,
        handleAddUser,
        handleRemoveUser,
        handleAddMultipleUsers,
        handleRemoveAllUsers,
        handlePageChange,
        handleInputChange,
        handleOnBlur,
        handleOnKeyDown,
        toggleCheckUser,
        toggleCheckAllUsers,
        toggleCheckSelectedUser,
        toggleCheckAllSelectedUsers,
        handleOpenInvitationModal,
        handleInviteSelectedUsers,
        closeInviteModal,
        handleExitPage,
        handleSaveUsers,
        handleClearFilters,
        onCloseWH,
        onClickRestoreIcon,
    };
};

export default useHubAddUsers;
